import * as React from "react";
import {
	Accordion, AccordionDetails, AccordionSummary, Alert, AppBar, FormLabel, InputAdornment, LinearProgress,
	ListItemButton, ListItemIcon, Paper, Toolbar, Backdrop, Box, Breadcrumbs, Button, CardActionArea, CardContent,
	Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
	FormControlLabel, FormGroup, FormHelperText, IconButton, InputLabel, Link, List, ListItem, ListItemText, MenuItem,
	Modal, OutlinedInput, Select, Snackbar, TextField, Tooltip, Typography, createFilterOptions, useMediaQuery,
	Grid, Avatar, Stack, Autocomplete, Divider
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/Info";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Save from "@mui/icons-material/Save";
import Inventory2 from "@mui/icons-material/Inventory2";
import AttachMoney from "@mui/icons-material/AttachMoney";
import Image from "@mui/icons-material/Image";
import Warehouse from "@mui/icons-material/Warehouse";
import Layers from "@mui/icons-material/Layers";
import LocalShipping from "@mui/icons-material/LocalShipping";
import Palette from "@mui/icons-material/Palette";
import Add from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import Close from "@mui/icons-material/Close";
import Check from "@mui/icons-material/Check";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { blue, green, purple, orange, indigo, teal, grey } from "@mui/material/colors";
import {
	SelectSaveProductCompleted,
	resetSaveProductCompleted,
	saveProductThunk,
	SelectSaveFailures,
	SAVE_STEP_LABELS
} from "./AddProductsSlice";
import VariantDialog from './VariantDialog';
import ImageGuidelinesDialog from "../ImageGuidelines/ImageGuidelinesDialog";
import { COLOR_NAMES, SIZE_CATEGORIES, getNearestColorName } from './helpers';
import { validateProduct, requiredMessage } from './utils/validateProduct';
import { buttonTheme } from "../../theme";
import { config } from "../../config";
import { getUserName } from "../../utils/authSession";
import { aiErrorMessage } from "../../utils/aiErrors";

/* ---------- constants & helpers ---------- */
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: { style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250 } }
};

const numberFieldProps = {
	type: "number",
	inputProps: { min: 0 },
	onWheel: (e) => e.currentTarget.blur(),
	onKeyDown: (e) => {
		if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
	},
};

const clampNonNeg = (setter) => (e) => {
	const v = e.target.value;
	setter(v === "" ? undefined : Math.max(0, Number(v)));
};

const toArray = (x) =>
	Array.isArray(x) ? x
		: typeof x === 'string' ? x.split(',').map(s => s.trim()).filter(Boolean)
			: [];

const deliveryOption = ["Store Pickup", "Home Delivery"];

const additionalInformations = [
	"Contains Added Preservatives",
	"Contains Chemicals",
	"Contains Lactose Substitutes",
	"contains Heavy Parts",
	"Fragile",
	"Keep Refrigerated",
	"Flammable",
	"Choking Hazard",
	"Gluten Free",
	"Vegan",
	"Organic",
	"Non-GMO",
	"Contains Nuts",
	"Sugar Free",
	"Batteries Included",
	"Batteries Not Included",
	"Assembly Required",
	"Waterproof",
	"Recyclable",
	"Biodegradable",
	"Eco-Friendly"
];

const useStyles = makeStyles(() => ({
	inputNumber: {
		"& input[type=number]": { "-moz-appearance": "text-field" },
		"& input[type=number]::-webkit-outer-spin-button": { "-webkit-appearance": "none", margin: 0 },
		"& input[type=number]::-webkit-inner-spin-button": { "-webkit-appearance": "none", margin: 0 }
	}
}));

const uploadBoxSx = {
	display: 'flex',
	border: '2px dashed',
	borderRadius: 2,
	height: { xs: 96, sm: 128 },
	bgcolor: '#fff',
	'&:hover': { borderColor: '#60a5fa' }
};

const previewSx = { width: '100%', height: 120, objectFit: 'cover', borderRadius: 2 };

const deleteBtnSx = { position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.7)' };

const buildVariantCode = ({ colorVariation, sizeVariation }) =>
	[colorVariation, sizeVariation].filter(Boolean).join(" • ") || "Untitled";

/* ---------- component ---------- */
export default function AddProducts() {
	/* ——— hooks / redux ——— */
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:750px)");
	const classes = useStyles();
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const addProductData = location.state;

	const sessionToken = () =>
		sessionStorage.getItem("sessionToken") || localStorage.getItem("sessionToken");

	const API_ROOT = config.API_MSWSSTOREMANAGER_BASE_URL;


	const MP_SEL_KEY = "mp.selection";

	const loadMpSelection = () => {
		try {
			return JSON.parse(sessionStorage.getItem(MP_SEL_KEY) || "{}");
		} catch {
			return {};
		}
	};

	const saveSelection = (sel) => {
		sessionStorage.setItem(MP_SEL_KEY, JSON.stringify(sel || {}));
	};

	const {
		departmentId: selectedDepartmentId = "",
		categoryId: selectedCategoryId = "",
		subCategoryId: selectedSubCategoryId = "",
	} = loadMpSelection();


	/* ——— prime AI draft ——— */
	React.useEffect(() => {
		if (!addProductData) return;

		// Accept both your old fields and the new AI schema (PN/PDN/SD/LD/PAC)
		const name = addProductData.aiProductName ?? addProductData.PN;
		const displayName = addProductData.aiProductDisplayName ?? addProductData.PDN;
		const shortDesc = addProductData.aiProductShortDescription ?? addProductData.SD;
		const longDesc = addProductData.aiProductDescription ?? addProductData.LD;
		const pac = addProductData.aiProductAllergicContent ?? addProductData.PAC;

		if (typeof name === 'string') setPName(name);
		if (typeof displayName === 'string') setPDisplayName(displayName);
		if (typeof shortDesc === 'string') setPShortDesc(shortDesc.slice(0, 250));
		if (typeof longDesc === 'string') setPDesc(longDesc.slice(0, 350));

		// PAC can be array or comma-separated string or absent
		setPAdditionalInfo(toArray(pac));
	}, [addProductData]);

	/* ---------- product-level state ---------- */
	const [pID] = React.useState(addProductData.productID);
	const [pName, setPName] = React.useState("");
	const [pDisplayName, setPDisplayName] = React.useState("");
	const [pSKU, setPSKU] = React.useState();
	const [pShortDesc, setPShortDesc] = React.useState("");
	const [pDesc, setPDesc] = React.useState("");
	const [pCSFlag, setPCSFlag] = React.useState(false);
	const [barCode, setBarCode] = React.useState();
	const [pricePerUnit, setPricePerUnit] = React.useState();
	const [units, setUnits] = React.useState();
	const [pQuantity, setPQuantity] = React.useState();
	const [pTotalPrice, setPTotalPrice] = React.useState();
	const [primary, setPrimary] = React.useState("");
	const [imgSec1, setImgSec1] = React.useState("");
	const [imgSec2, setImgSec2] = React.useState("");
	const [imgSec3, setImgSec3] = React.useState("");
	const [imgSec4, setImgSec4] = React.useState("");
	const [maxStock, setMaxStock] = React.useState();
	const [pInStock, setPInStock] = React.useState();
	const [pLowStock, setPLowStock] = React.useState();
	const [pLowStockTrack, setPLowStockTrack] = React.useState(false);
	const [pFulfillment, setPFulfillment] = React.useState("");
	const [pStatus, setPStatus] = React.useState("");
	const [pDeliveryOption, setPDeliveryOption] = React.useState([]);
	const [pShippingWeight, setPShippingWeight] = React.useState();
	const [pAdditionalInfo, setPAdditionalInfo] = React.useState([]);
	const [pColor, setPColor] = React.useState("");
	const [pSize, setPSize] = React.useState("");
	const [pColorValue, setPColorValue] = React.useState("");
	const [supplierId, setSupplierId] = React.useState();
	const [companyName, setCompanyName] = React.useState();
	const [contactName, setContactName] = React.useState();
	const [contactEmail, setContactEmail] = React.useState();
	const [contactPhone, setContactPhone] = React.useState();
	const [websiteURL, setWebsiteURL] = React.useState();
	const [addrLine1, setAddrLine1] = React.useState();
	const [addrLine2, setAddrLine2] = React.useState();
	const [city, setCity] = React.useState();
	const [stateProvince, setStateProvince] = React.useState();
	const [postalCode, setPostalCode] = React.useState();
	const [country, setCountry] = React.useState();

	const [formErrors, setFormErrors] = React.useState([]);
	const [openSaveProduct, setOpenSaveProduct] = React.useState(false);

	/* ——— snackbars / dialogs ——— */
	const saveProductCompleted = useSelector(SelectSaveProductCompleted);
	const saveFailures = useSelector(SelectSaveFailures);
	const [saveFailureOpen, setSaveFailureOpen] = React.useState(false);
	const lastPayloadRef = React.useRef(null);
	const [saveDialog, setSaveDialog] = React.useState(saveProductCompleted);
	const [openSaveProductSnackBar, setOpenSaveProductSnackbar] = React.useState(false);
	const [openSaveVariantSnackBar, setOpenSaveVariantSnackbar] = React.useState(false);
	const [loadBackdrop, setLoadBackdrop] = React.useState(false);

	const [openDuplicateImgSnackbar, setOpenDuplicateImgSnackbar] = React.useState(false);

	const [openEnhanceWarn, setOpenEnhanceWarn] = React.useState(false);
	const [aiEnhanceError, setAiEnhanceError] = React.useState("");
	const handleCloseEnhanceWarn = (_, r) => r !== 'clickaway' && setOpenEnhanceWarn(false);

	const handleCloseDuplicateImgSnackbar = (_, r) =>
		r !== 'clickaway' && setOpenDuplicateImgSnackbar(false);

	const handleCloseProductSnackbar = (_, r) => r !== "clickaway" && setOpenSaveProductSnackbar(false);
	const handleCloseVariantSnackbar = (_, r) => r !== "clickaway" && setOpenSaveVariantSnackbar(false);

	React.useEffect(() => {
		if (saveProductCompleted) {
			setSaveDialog(true);
			setOpenSaveProductSnackbar(true);
			setLoadBackdrop(false);
			dispatch(resetSaveProductCompleted());
		}
	}, [saveProductCompleted, dispatch]);

	/* ---------- accordion controls ---------- */
	const { department: subCategory } = useParams();
	const [expanded, setExpanded] = React.useState("panel1");
	const handleChange = (panel) => (_, isEx) => setExpanded(isEx ? panel : false);

	/* ---------- image upload refs ---------- */
	const uploadInputRefs = React.useMemo(
		() => Array.from({ length: 5 }, () => React.createRef()),
		[]
	);

	/* ---------- product-image state & helpers ------------------ */
	const [imageFiles, setImageFiles] = React.useState([]);
	const [images, setImages] = React.useState([]);     // array of preview URLs

	const isDuplicateFile = (file, list) =>
		list.some((f) => f && f.name === file.name && f.size === file.size);

	/** add / replace image in slot `idx`  (auto-truncate to 5) */
	const handleImageUpload = (e, idx) => {
		const file = e.target.files?.[0];
		if (!file) return;

		if (isDuplicateFile(file, imageFiles)) {
			setOpenDuplicateImgSnackbar(true);
			return;
		}

		if (file.size / 1024 / 1024 > 2) {
			alert('File size exceeds 2 MiB');
			return;
		}

		// show preview
		setImages(prev => {
			const next = [...prev];
			next[idx] = URL.createObjectURL(file);
			return next.slice(0, 5);
		});

		setImageFiles(prev => {
			const next = [...prev];
			next[idx] = file;
			return next.slice(0, 5);
		});
	};


	/** delete and *shift left* so there are no gaps */
	const removeImage = (idx) => {
		setImages(prev => {
			const url = prev[idx];
			if (url) URL.revokeObjectURL(url);
			return prev.filter((_, i) => i !== idx);
		});
		setImageFiles(prev => prev.filter((_, i) => i !== idx));
	};
	/* ---------- preview images ---------- */

	/* dialogs per preview */
	const [open1, setOpen1] = React.useState(false);
	const [open2, setOpen2] = React.useState(false);
	const [open3, setOpen3] = React.useState(false);
	const [open4, setOpen4] = React.useState(false);
	const [open5, setOpen5] = React.useState(false);

	const [guideline, setGuideline] = React.useState(false);

	/* ---------- auto price calc ---------- */
	React.useEffect(() => {
		if (!pCSFlag && pricePerUnit && units && pQuantity) {
			setPTotalPrice(parseFloat(pricePerUnit) * parseFloat(units) * parseFloat(pQuantity));
		}
	}, [pCSFlag, pricePerUnit, units, pQuantity]);

	/* ---------- variation state ---------- */
	const [expandedVariation, setExpandedVariation] = React.useState("variantPanel0");
	const handleVariationPanelChange = (p) => (_, ex) => setExpandedVariation(ex ? p : false);
	const [expandedVariationEdit, setExpandedVariationEdit] = React.useState("variantEditPanel0");
	const handleVariationPanelEditChange = (p) => (_, ex) => setExpandedVariationEdit(ex ? p : false);

	const [variantIdValue, setVariantIdValue] = React.useState(null);
	const [variantCode, setVariantCode] = React.useState(null);

	const [variationDetails, setVariationDetails] = React.useState({
		variationSKU: "",
		variationCSFlag: false,
		variationBarCode: "",
		variationPricePerUnit: "",
		variationUnits: "",
		variationQuantity: "",
		variationTotalPrice: "",
		variationImgPrimary: "",
		variationImgSec1: "",
		variationImgSec2: "",
		variationImgSec3: "",
		variationImgSec4: "",
		variationMaxStock: "",
		variationInStock: "",
		variationLowStock: "",
		variationLowStockTrack: false,
		colorVariation: "", color: "", colorHex: "", sizeVariation: ""
	});

	const resetVariationDetails = () => {
		setVariationDetails({
			variationSKU: "",
			variationCSFlag: false,
			variationBarCode: "",
			variationPricePerUnit: "",
			variationUnits: "",
			variationQuantity: "",
			variationTotalPrice: "",
			variationImgPrimary: "",
			variationImgSec1: "",
			variationImgSec2: "",
			variationImgSec3: "",
			variationImgSec4: "",
			variationMaxStock: "",
			variationInStock: "",
			variationLowStock: "",
			variationLowStockTrack: false,
			colorVariation: "",
			color: "",
			sizeVariation: ""
		});
		setVariantPreferenceColorCodeId(null);
		setVariantPreferenceSizeId(null);
		setVariantPreferenceColorId(null);
		setVariantPricingId(null);
		setVariantInventoryId(null);
		setVariantPrimary(null);
		setVariantSec1(null);
		setVariantSec2(null);
		setVariantSec3(null);
		setVariantSec4(null);
	};

	/* preview images for each variant (5 max, mirror the product-level ones) */
	const [variantPrimary, setVariantPrimary] = React.useState(null);
	const [variantSec1, setVariantSec1] = React.useState(null);
	const [variantSec2, setVariantSec2] = React.useState(null);
	const [variantSec3, setVariantSec3] = React.useState(null);
	const [variantSec4, setVariantSec4] = React.useState(null);

	/* hidden file inputs for variant images */
	const variantInput1 = React.useRef(null);
	const variantInput2 = React.useRef(null);
	const variantInput3 = React.useRef(null);
	const variantInput4 = React.useRef(null);
	const variantInput5 = React.useRef(null);

	const [variantPreferenceColorCodeId, setVariantPreferenceColorCodeId] = React.useState(null);
	const [variantPreferenceSizeId, setVariantPreferenceSizeId] = React.useState(null);
	const [variantPreferenceColorId, setVariantPreferenceColorId] = React.useState(null);
	const [variantPricingId, setVariantPricingId] = React.useState(null);
	const [variantInventoryId, setVariantInventoryId] = React.useState(null);

	const sections = [
		{ id: "panel1", label: "Product Details", icon: Inventory2 },
		{ id: "panel2", label: "Pricing", icon: AttachMoney },
		{ id: "panel3", label: "Images", icon: Image },
		{ id: "panel4", label: "Inventory", icon: Warehouse },
		{ id: "panel5", label: "Variations", icon: Layers },
		{ id: "panel6", label: "Shipping", icon: LocalShipping },
		{ id: "panel7", label: "Additional", icon: InfoIcon }
	];

	const totalSections = sections.length;

	const completedSectionIds = React.useMemo(() => {
		const ok = {
			panel1: !!(pName && pDisplayName && pShortDesc && pDesc),
			panel2: !!(barCode && pricePerUnit && units && pQuantity),
			panel3: images.length > 0,
			panel4: !!(maxStock && pInStock && pLowStock),
			panel5: true,
			panel6: !!(pFulfillment && pStatus && pDeliveryOption.length),
			panel7: true,
		};
		return sections.filter((s) => ok[s.id]).map((s) => s.id);
	}, [
		pName, pDisplayName,
		barCode, pricePerUnit, units, pQuantity,
		images,
		maxStock, pInStock, pLowStock,
		pFulfillment, pStatus, pDeliveryOption
	]);

	const handleAiEnhanceClick = () => {
		if (!pName.trim() || !pDesc.trim()) {
			setOpenEnhanceWarn(true);
			return;                     // ⬅️ skip the API call
		}

		setLoadBackdrop(true);
		fetch(
			`${config.API_MWSSTOREMANAGERAI_BASE_URL}/api/v1/enhance-product-info`,
			{
				method: "POST",
				body: JSON.stringify({
					productName: `${pName}`,
					productCategory: `${addProductData.subCategory}`,
					productDescription: `${pDesc}`
				}),
				headers: {
					Accept: "*/*",
					"Cache-Control": "no-cache",
					"Content-Type": "application/json", "X-Session-Token": sessionToken()
				}
			}
		)
			.then((res) => {
				const aiMsg = aiErrorMessage(res.status);
				if (aiMsg) {
					const aiErr = new Error(aiMsg);
					aiErr.userMessage = aiMsg;
					throw aiErr;
				}
				return res.json();
			})
			.then((data) => {
				setLoadBackdrop(false);
				if (!data.error) {
					setPDesc(data.description.slice(0, 350));
					setPDisplayName(data.name.slice(0, 200));
				}
			})
			.catch((error) => {
				setLoadBackdrop(false);
				if (error.userMessage) setAiEnhanceError(error.userMessage);
			});
	};

	const completedSections = completedSectionIds.length;
	const progressValue = (completedSections / totalSections) * 100;
	const completed = completedSectionIds.reduce((o, id) => ({ ...o, [id]: true }), {});

	const allRequiredMet = completedSections >= 5 && formErrors.length === 0;

	React.useEffect(() => {
		if (!formErrors.length) return;
		const productState = {
			pName, pDisplayName, barCode, pricePerUnit, units, pQuantity,
			pTotalPrice, maxStock, pInStock, pLowStock,
			pFulfillment, pStatus, pDeliveryOption, pShippingWeight
		};
		const { ok } = validateProduct(productState);
		if (ok) setFormErrors([]);
	}, [
		pName, pDisplayName, barCode, pricePerUnit, units, pQuantity,
		pTotalPrice, maxStock, pInStock, pLowStock,
		pFulfillment, pStatus, pDeliveryOption, pShippingWeight, formErrors.length
	]);

	/* upload helpers (identical size checks to the product images) */
	const handleVariantPrimaryUpload = (e) => {
		const f = e.target.files?.[0];
		if (!f) return;
		if (isDuplicateFile(f, [
			variationDetails.variationImgPrimary,
			variationDetails.variationImgSec1,
			variationDetails.variationImgSec2,
			variationDetails.variationImgSec3,
			variationDetails.variationImgSec4
		])) {
			setOpenDuplicateImgSnackbar(true);
			return;
		}
		if (f.size / 1024 / 1024 > 2) return alert("File size exceeds 2 MiB");
		setVariantPrimary(URL.createObjectURL(f));
		setVariationDetails((p) => ({ ...p, variationImgPrimary: f }));
	};
	const handleVariantSec1Upload = (e) => {
		const f = e.target.files?.[0];
		if (!f) return;
		if (isDuplicateFile(f, [
			variationDetails.variationImgPrimary,
			variationDetails.variationImgSec1,
			variationDetails.variationImgSec2,
			variationDetails.variationImgSec3,
			variationDetails.variationImgSec4
		])) {
			setOpenDuplicateImgSnackbar(true);
			return;
		}
		if (f.size / 1024 / 1024 > 2) return alert("File size exceeds 2 MiB");
		setVariantSec1(URL.createObjectURL(f));
		setVariationDetails((p) => ({ ...p, variationImgSec1: f }));
	};
	const handleVariantSec2Upload = (e) => {
		const f = e.target.files?.[0];
		if (!f) return
		if (isDuplicateFile(f, [
			variationDetails.variationImgPrimary,
			variationDetails.variationImgSec1,
			variationDetails.variationImgSec2,
			variationDetails.variationImgSec3,
			variationDetails.variationImgSec4
		])) {
			setOpenDuplicateImgSnackbar(true);
			return;
		}
		if (f.size / 1024 / 1024 > 2) return alert("File size exceeds 2 MiB");
		setVariantSec2(URL.createObjectURL(f));
		setVariationDetails((p) => ({ ...p, variationImgSec2: f }));
	};
	const handleVariantSec3Upload = (e) => {
		const f = e.target.files?.[0];
		if (!f) return;
		if (isDuplicateFile(f, [
			variationDetails.variationImgPrimary,
			variationDetails.variationImgSec1,
			variationDetails.variationImgSec2,
			variationDetails.variationImgSec3,
			variationDetails.variationImgSec4
		])) {
			setOpenDuplicateImgSnackbar(true);
			return;
		}
		if (f.size / 1024 / 1024 > 2) return alert("File size exceeds 2 MiB");
		setVariantSec3(URL.createObjectURL(f));
		setVariationDetails((p) => ({ ...p, variationImgSec3: f }));
	};
	const handleVariantSec4Upload = (e) => {
		const f = e.target.files?.[0];
		if (!f) return;
		if (isDuplicateFile(f, [
			variationDetails.variationImgPrimary,
			variationDetails.variationImgSec1,
			variationDetails.variationImgSec2,
			variationDetails.variationImgSec3,
			variationDetails.variationImgSec4
		])) {
			setOpenDuplicateImgSnackbar(true);
			return;
		}
		if (f.size / 1024 / 1024 > 2) return alert("File size exceeds 2 MiB");
		setVariantSec4(URL.createObjectURL(f));
		setVariationDetails((p) => ({ ...p, variationImgSec4: f }));
	};

	/* global modal toggles */
	const [openVariationModal, setOpenVariationModal] = React.useState(false);
	const [openVariationModalEdit, setOpenVariationModalEdit] = React.useState(false);

	/* list of variants (card view) */
	const [variationToShow, setVariationToShow] = React.useState([]);

	const refreshVariants = async () => {
		const res = await fetch(
			`${API_ROOT}/v2/variant/by-product/${addProductData.productID}?vendorId=${encodeURIComponent(addProductData.vendorId)}`,
			{ headers: JSON_HEADERS }
		);
		const list = await res.json();           // whatever the service returns
		setVariationToShow(list);                // overwrite local cache
	};

	const deleteVariant = (id) => setVariationToShow(variationToShow.filter((v) => v.id !== id));

	/* ---------- color & size helpers ---------- */
	const colorNamesMap = COLOR_NAMES;
	const sizeCategories = SIZE_CATEGORIES;

	const sizeOptions = [];
	Object.entries(sizeCategories).forEach(([cat, sub]) => {
		Object.entries(sub).forEach(([subCat, sizes]) => {
			sizes.forEach((s) => sizeOptions.push({ category: cat, label: `${subCat}: ${s}`, value: `${cat}-${subCat}-${s}` }));
		});
	});

	const colourSwatches = [
		'#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6',
		'#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
		'#f43f5e', '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#ffffff'
	];

	const handleProductColourSelect = (hex, custom = false) => {
		setPColorValue(hex);
		if (custom) {
			setPColor(getNearestColorName(hex));   // may be “Custom Color”
		} else {
			const named = getNearestColorName(hex);
			setPColor(named);
		}
	};

	const filterOptions = createFilterOptions();

	const handleVariationInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		if (value === "custom-color") {
			return;
		}
		if (name === "colorVariation") {
			if (!value) {
				setVariationDetails((p) => ({ ...p, colorVariation: "" }));
			} else {
				const rgb = colorNamesMap[value];
				setVariationDetails((p) => ({ ...p, colorVariation: value, color: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` }));
			}
		} else {
			setVariationDetails((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
		}
	};


	/* ---------- price auto calc for variation ---------- */
	React.useEffect(() => {
		if (!variationDetails.variationCSFlag) {
			const ppu = parseFloat(variationDetails.variationPricePerUnit) || 0;
			const un = parseFloat(variationDetails.variationUnits) || 0;
			const qty = parseFloat(variationDetails.variationQuantity) || 0;
			setVariationDetails((prev) => ({ ...prev, variationTotalPrice: ppu * un * qty }));
		}
	}, [
		variationDetails.variationCSFlag,
		variationDetails.variationPricePerUnit,
		variationDetails.variationUnits,
		variationDetails.variationQuantity
	]);

	/* ---------- product / pricing / inventory objects ---------- */
	const productDetails = {
		id: addProductData.productID,
		vendorId: addProductData.vendorId,
		department: addProductData.department,
		category: addProductData.category,
		subCategory: addProductData.subCategory,
		name: pName,
		displayName: pDisplayName,
		shortDescription: pShortDesc,
		description: pDesc,
		creationDateTime: Date.now(),
		createdBy: getUserName(),
		updatedDateTime: Date.now(),
		updatedBy: getUserName()
	};
	const pricingDetails = {
		productId: addProductData.productID,
		vendorId: addProductData.vendorId,
		smProductPricing: {
			id: addProductData.pricingID,
			barcode: barCode,
			totalPrice: pTotalPrice,
			pricePerUnit: pricePerUnit,
			units: units,
			quantity: pQuantity,
			contactSupplierFlag: pCSFlag,
			creationDateTime: Date.now(),
			createdBy: getUserName(),
			updatedDateTime: Date.now(),
			updatedBy: getUserName(),
			sku: pSKU
		}
	};
	const inventoryDetails = {
		productId: addProductData.productID,
		vendorId: addProductData.vendorId,
		smProductInventory: {
			id: addProductData.inventoryID,
			barcode: barCode,
			inStock: pInStock,
			lowStock: pLowStock,
			lowStockTrack: pLowStockTrack,
			maxStock: maxStock,
			creationDateTime: Date.now(),
			createdBy: getUserName(),
			updatedDateTime: Date.now(),
			updatedBy: getUserName(),
			sku: pSKU
		}
	};

	const productPreference = {
		productId: addProductData.productID,
		smProductPreference: [
			{ type: 'color', value: pColor ?? '' },
			{ type: 'colorCode', value: pColorValue ?? '' },
			{ type: 'size', value: pSize ?? '' }
		]
	};

	const uploadImage = (file, imageType, variantId) => {
		// only upload if we actually have a File/Blob (not a presigned URL string)
		if (!(file instanceof File || file instanceof Blob)) {
			return Promise.resolve();
		}

		const fd = new FormData();
		fd.append('bucketname', 'microstores.net');
		fd.append('uploadfile', file);
		fd.append('productid', addProductData.productID);
		fd.append('vendorid', addProductData.vendorId);
		fd.append('imageType', imageType);
		fd.append('variantId', variantId);

		return fetch(`${API_ROOT}/v2/variant-images/uploadProductMediaFile`, {
			method: 'POST',
			body: fd,
			headers: { Accept: '*/*', 'Cache-Control': 'no-cache', "X-Session-Token": sessionToken() }  // *no* Content-Type for FormData
		});
	};

	/* ---------- SAVE PRODUCT – COMPLETE HANDLER ---------- */
	const onSaveProduct = React.useCallback(async () => {
		/* ---------- 1. gather raw state & validate ---------- */
		const productState = {
			pName, pDisplayName, barCode, pricePerUnit, units, pQuantity,
			pTotalPrice, maxStock, pInStock, pLowStock,
			pFulfillment, pStatus, pDeliveryOption, pShippingWeight
		};

		const { ok, missing } = validateProduct(productState);
		if (!ok) {
		setFormErrors(missing.map(requiredMessage));
			setOpenSaveProduct(true);
			return;
		}
		setFormErrors([]);                 // clear any old errors
		setOpenSaveProduct(false);

		const fulfillmentDetails = {
			productId: addProductData.productID,
			vendorId: addProductData.vendorId,
			smProductFulfilment: {
				id: addProductData.fulfillmentID,
				deliveryOption: pDeliveryOption.join(','),
				fulfillment: pFulfillment,
				productStatus: pStatus,
				creationDateTime: Date.now(),
				createdBy: getUserName(),
				updatedDateTime: Date.now(),
				updatedBy: getUserName()
			}
		};

		const productPreference = {
			productId: addProductData.productID,
			smProductPreference: [
				{ type: 'color', value: pColor ?? '' },
				{ type: 'colorCode', value: pColorValue ?? '' },
				{ type: 'size', value: pSize ?? '' }
			]
		};

		const payload = {
			subCategory,
			productID: addProductData.productID,
			productDetails,
			pricingDetails,
			inventoryDetails,
			fulfillmentDetails,
			imageFiles: {
				primary: imageFiles[0] ?? null,
				imgSec1: imageFiles[1] ?? null,
				imgSec2: imageFiles[2] ?? null,
				imgSec3: imageFiles[3] ?? null,
				imgSec4: imageFiles[4] ?? null
			},
			additionalInformation: {
				productId: addProductData.productID,
				vendorId: addProductData.vendorId,
				smProductAdditionalInfo: {
					id: addProductData.additionalInfoID,
					shippingWeight: pShippingWeight,
					specialIndicators: pAdditionalInfo.join(','),
					creationDateTime: Date.now(),
					createdBy: getUserName(),
					updatedDateTime: Date.now(),
					updatedBy: getUserName()
				}
			},
			productPreference
		};

		/* ---------- 3. dispatch thunk ---------- */
		lastPayloadRef.current = payload;
		setLoadBackdrop(true);
		try {
			await dispatch(saveProductThunk(payload)).unwrap();
			setSaveDialog(true);
			setOpenSaveProductSnackbar(true);
		} catch (err) {
			console.error(err);
			setSaveFailureOpen(true);
		} finally {
			setLoadBackdrop(false);
		}
	}, [
		pName, pDisplayName, barCode, pricePerUnit, units, pQuantity,
		pTotalPrice, maxStock, pInStock, pLowStock,
		pFulfillment, pStatus, pDeliveryOption, pShippingWeight,
		pColor, pColorValue, pSize, pCSFlag, pSKU,
		pShortDesc, pDesc,
		pAdditionalInfo, imageFiles,
		addProductData, productDetails, pricingDetails, inventoryDetails,
		dispatch, subCategory
	]);


	/* ───────────────────────── add NEW variant ───────────────────── */
	const retryFailedSave = async () => {
		if (!lastPayloadRef.current) return;
		const retrySteps = saveFailures.map((f) => f.step);
		setSaveFailureOpen(false);
		setLoadBackdrop(true);
		try {
			await dispatch(saveProductThunk({ ...lastPayloadRef.current, retrySteps })).unwrap();
			setSaveDialog(true);
			setOpenSaveProductSnackbar(true);
		} catch (err) {
			console.error(err);
			setSaveFailureOpen(true);
		} finally {
			setLoadBackdrop(false);
		}
	};

	const handleAddVariant = async () => {
		resetVariationDetails();
		try {
			setLoadBackdrop(true);
			const res = await fetch(`${API_ROOT}/v2/variant/add`, {
				method: 'POST',
				headers: JSON_HEADERS,
				body: JSON.stringify({
					productId: addProductData.productID,
					variantCode: '',
					createdBy: addProductData.vendorId
				})
			});
			const data = await res.json();
			setVariantIdValue(data.id);
			setVariantCode('');
			setOpenVariationModal(true);
		} catch (err) {
			console.error(err);
			alert('Could not start a new variant - please try again.');
		} finally {
			setLoadBackdrop(false);
		}
	};


	const editVariant = async (variant) => {
		try {
			setLoadBackdrop(true);
			const res = await fetch(
				`${API_ROOT}/v2/variant-details?vendorId=${encodeURIComponent(addProductData.vendorId)}`,
				{
					method: 'POST',
					headers: JSON_HEADERS,
					body: JSON.stringify({
						productId: variant.productId,
						variant_id: variant.id          // body stays the same
					})
				}
			);
			const data = await res.json();

			/* 2️⃣  hoist the IDs so Update can reuse them */
			setVariantIdValue(variant.id);
			setVariantPricingId(data.smProductVariantPricingResponseDTO.id);
			setVariantInventoryId(data.smProductVariantInventoryResponseDTO.id);

			/* 3️⃣  convert BE fields → dialog-state shape */
			setVariationDetails({
				/* ----- pricing ----- */
				variationBarCode: data.smProductVariantPricingResponseDTO.barcode,
				variationPricePerUnit: data.smProductVariantPricingResponseDTO.pricePerUnit,
				variationTotalPrice: data.smProductVariantPricingResponseDTO.totalPrice,
				variationUnits: data.smProductVariantPricingResponseDTO.units,
				variationQuantity: data.smProductVariantPricingResponseDTO.quantity,
				variationSKU: data.smProductVariantPricingResponseDTO.sku,
				/* ----- inventory ---- */
				variationInStock: data.smProductVariantInventoryResponseDTO.inStock,
				variationLowStock: data.smProductVariantInventoryResponseDTO.lowStock,
				variationLowStockTrack: data.smProductVariantInventoryResponseDTO.lowStockTrack,
				variationMaxStock: data.smProductVariantInventoryResponseDTO.maxStock,
				/* we’ll fill colour / size below */
			});

			/* 4️⃣  images */
			data.smProductVariantImagesV2ResponseDTO.forEach((img) => {
				switch (img.imageType) {
					case 'primary':
						setVariantPrimary(img.presignedURL);
						setVariationDetails(p => ({ ...p, variationImgPrimary: img.presignedURL }));
						break;
					case 'imgSec1':
						setVariantSec1(img.presignedURL);
						setVariationDetails(p => ({ ...p, variationImgSec1: img.presignedURL }));
						break;
					case 'imgSec2':
						setVariantSec2(img.presignedURL);
						setVariationDetails(p => ({ ...p, variationImgSec2: img.presignedURL }));
						break;
					case 'imgSec3':
						setVariantSec3(img.presignedURL);
						setVariationDetails(p => ({ ...p, variationImgSec3: img.presignedURL }));
						break;
					case 'imgSec4':
						setVariantSec4(img.presignedURL);
						setVariationDetails(p => ({ ...p, variationImgSec4: img.presignedURL }));
						break;
					default:
				}
			});

			/* 5️⃣  prefs: colour / size */
			data.smProductVariantPreferenceResponseDTO.forEach((pref) => {
				switch (pref.type) {
					case 'color':
						setVariantPreferenceColorId(pref.id);
						setVariationDetails(p => ({ ...p, colorVariation: pref.value }));
						break;
					case 'colorCode':
						setVariantPreferenceColorCodeId(pref.id);
						setVariationDetails(p => ({ ...p, color: pref.value }));
						break;
					case 'size':
						setVariantPreferenceSizeId(pref.id);
						setVariationDetails(p => ({ ...p, sizeVariation: pref.value }));
						break;
					default:
				}
			});

			setOpenVariationModalEdit(true);   // ⬅️ finally open dialog
		} catch (err) {
			console.error(err);
			alert('Unable to load variant details – please try again.');
		} finally {
			setLoadBackdrop(false);
		}
	};


	/* the JSON headers you used everywhere */
	const JSON_HEADERS = {
		Accept: '*/*',
		'Cache-Control': 'no-cache',
		'Content-Type': 'application/json',
		"X-Session-Token": sessionToken()
	};

	// --- helpers for validation and coercion ---
	const must = (v) => v !== undefined && v !== null && v !== '';
	const asNum = (v) => Number(v); // assumes required fields are present

	function validatePricingInput(d) {
		const missing = [];
		if (!must(d.variationBarCode)) missing.push('Barcode');
		if (!must(d.variationTotalPrice)) missing.push('Total Price');
		if (!must(d.variationPricePerUnit)) missing.push('Price Per Unit');
		if (!must(d.variationUnits)) missing.push('Units');
		if (!must(d.variationQuantity)) missing.push('Quantity');
		if (!must(d.variationSKU)) missing.push('SKU');
		return { ok: missing.length === 0, missing };
	}

	function buildPricingBody(base) {
		return {
			variantId: Number(base.variantId),
			productId: Number(base.productId),
			barcode: String(base.barcode),
			totalPrice: asNum(base.totalPrice),
			pricePerUnit: asNum(base.pricePerUnit),
			units: asNum(base.units),
			quantity: asNum(base.quantity),
			sku: String(base.sku),
			contactSupplierFlag: Boolean(base.contactSupplierFlag),
			createdBy: String(base.createdBy),
		};
	}

	// --- API calls: PRICING ---
	async function pricingCreate(payload) {
		const body = buildPricingBody(payload);
		const res = await fetch(
			`${API_ROOT}/v2/variant-pricing?vendorId=${encodeURIComponent(addProductData.vendorId)}`, {
			method: 'POST',
			headers: {
				Accept: "*/*",
				'Content-Type': 'application/json',
				'X-Session-Token': sessionToken(),
			},
			body: JSON.stringify(body),
		});
		if (!res.ok) {
			const text = await res.text();
			throw new Error(`variant-pricing POST ${res.status}: ${text}`);
		}
		return res.json();
	}

	async function pricingUpdate(id, payload) {
		const body = buildPricingBody(payload);
		const res = await fetch(
			`${API_ROOT}/v2/variant-pricing/${encodeURIComponent(id)}?vendorId=${encodeURIComponent(addProductData.vendorId)}`, {
			method: 'PUT',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json',
				'X-Session-Token': sessionToken(),
			},
			body: JSON.stringify({ id: Number(id), ...body }),
		});
		if (!res.ok) {
			const text = await res.text();
			throw new Error(`variant-pricing PUT ${res.status}: ${text}`);
		}
		return res.json();
	}

	// keep these below the pricing helpers
	const variantUpdate = (variantId, variantCode) =>
		fetch(`${API_ROOT}/v2/variant/update`, {
			method: 'POST',
			body: JSON.stringify({
				id: variantId,
				variantCode,
				updatedBy: addProductData.vendorId,
				lastUpdated: new Date().toISOString()
			}),
			headers: JSON_HEADERS
		}).then(r => r.json());

	const inventoryCreate = (payload) =>
		fetch(
			`${API_ROOT}/v2/variant-inventories?vendorId=${encodeURIComponent(addProductData.vendorId)}`, {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: JSON_HEADERS
		}).then(r => r.json());

	const inventoryUpdate = (inventoryId, payload) =>
		fetch(
			`${API_ROOT}/v2/variant-inventories/${inventoryId}?vendorId=${encodeURIComponent(addProductData.vendorId)}`, {
			method: 'PUT',
			body: JSON.stringify({ id: inventoryId, ...payload }),
			headers: JSON_HEADERS
		}).then(r => r.json());

	/* preferences: POST (add) | PUT (update) */
	const preferenceRequest = (method, id /* null for add */, payload) => {
		const url = method === 'POST'
			? `${API_ROOT}/v2/variant-preferences/add?vendorId=${encodeURIComponent(addProductData.vendorId)}`
			: `${API_ROOT}/v2/variant-preferences/${id}?vendorId=${encodeURIComponent(addProductData.vendorId)}`;

		return fetch(url, {
			method,
			body: JSON.stringify(payload),
			headers: JSON_HEADERS
		}).then(r => r.json());
	};


	/* ───────────────────────── add NEW variant ───────────────────── */
	/* ───────────────────────── add NEW variant ───────────────────── */
	const handleAddVariantData = async (dataObj) => {
		try {
			setLoadBackdrop(true);

			// sanity: make sure we created a variant shell via "Add Variant"
			if (!variantIdValue) {
				alert('Variant not initialized yet. Click “Add Variant” again.');
				return;
			}

			const variantId = variantIdValue;
			const vCode = buildVariantCode(dataObj);

			// 1) header
			await variantUpdate(variantId, vCode);

			// 2) pricing (validate once)
			const v = validatePricingInput(dataObj);
			if (!v.ok) {
				setLoadBackdrop(false);
				alert(`Please fill in all required fields:\n- ${v.missing.join('\n- ')}`);
				return;
			}
			await pricingCreate({
				variantId,
				productId: addProductData.productID,
				barcode: dataObj.variationBarCode,
				totalPrice: dataObj.variationTotalPrice,
				pricePerUnit: dataObj.variationPricePerUnit,
				units: dataObj.variationUnits,
				quantity: dataObj.variationQuantity,
				sku: dataObj.variationSKU,
				contactSupplierFlag: false,
				createdBy: addProductData.vendorId,
			});

			// 3) inventory
			await inventoryCreate({
				variantId,
				productId: addProductData.productID,
				barcode: dataObj.variationBarCode,
				sku: dataObj.variationSKU,
				inStock: dataObj.variationInStock,
				lowStock: dataObj.variationLowStock,
				lowStockTrack: dataObj.variationLowStockTrack,
				maxStock: dataObj.variationMaxStock,
				createdBy: addProductData.vendorId
			});

			// 4) preferences
			const makePref = (type, value) => ({
				variantId,
				productId: addProductData.productID,
				type,
				value,
				createdBy: addProductData.vendorId
			});
			if (dataObj.colorVariation) await preferenceRequest('POST', null, makePref('color', dataObj.colorVariation));
			if (dataObj.color) await preferenceRequest('POST', null, makePref('colorCode', dataObj.color));
			if (dataObj.sizeVariation) await preferenceRequest('POST', null, makePref('size', dataObj.sizeVariation));

			// 5) images
			await Promise.all([
				uploadImage(dataObj.variationImgPrimary, 'imgPrimary', variantId),
				uploadImage(dataObj.variationImgSec1, 'imgSec1', variantId),
				uploadImage(dataObj.variationImgSec2, 'imgSec2', variantId),
				uploadImage(dataObj.variationImgSec3, 'imgSec3', variantId),
				uploadImage(dataObj.variationImgSec4, 'imgSec4', variantId)
			]);

			// 6) refresh UI
			setVariationToShow(prev => [
				...prev,
				{
					id: variantId, ...dataObj, variantCode: vCode,
					variantPricingId: null,
					variantInventoryId: null,
					variantPreferenceColorId: null,
					variantPreferenceColorCodeId: null,
					variantPreferenceSizeId: null
				}
			]);
			// await refreshVariants();

			setOpenVariationModal(false);
			setOpenSaveVariantSnackbar(true);
		} catch (err) {
			console.error(err);
			alert('Failed to save variant – please try again.');
		} finally {
			setLoadBackdrop(false);
		}
	};

	const handleSaveVariantData = async (dataObj) => {
		try {
			setLoadBackdrop(true);

			// header
			const variantCodeStr = buildVariantCode(dataObj);
			await variantUpdate(variantIdValue, variantCodeStr);

			// PRICING (validate once, then create or update)
			{
				const v = validatePricingInput(dataObj);
				if (!v.ok) {
					setLoadBackdrop(false);
					alert(`Please fill in all required fields:\n- ${v.missing.join('\n- ')}`);
					return;
				}

				const pricingPayload = {
					variantId: variantIdValue,
					productId: addProductData.productID,
					barcode: dataObj.variationBarCode,
					totalPrice: dataObj.variationTotalPrice,
					pricePerUnit: dataObj.variationPricePerUnit,
					units: dataObj.variationUnits,
					quantity: dataObj.variationQuantity,
					sku: dataObj.variationSKU,
					contactSupplierFlag: false,
					createdBy: addProductData.vendorId,
				};

				if (variantPricingId) {
					await pricingUpdate(variantPricingId, pricingPayload);
				} else {
					await pricingCreate(pricingPayload);
				}
			}

			// INVENTORY
			const inventoryPayload = {
				variantId: variantIdValue,
				productId: addProductData.productID,
				barcode: dataObj.variationBarCode,
				sku: dataObj.variationSKU,
				inStock: dataObj.variationInStock,
				lowStock: dataObj.variationLowStock,
				lowStockTrack: dataObj.variationLowStockTrack,
				maxStock: dataObj.variationMaxStock,
				createdBy: addProductData.vendorId
			};
			if (variantInventoryId) {
				await inventoryUpdate(variantInventoryId, inventoryPayload);
			} else {
				await inventoryCreate(inventoryPayload);
			}

			// PREFS
			const prefBase = (type, value) => ({
				variantId: variantIdValue,
				productId: addProductData.productID,
				type,
				value,
				createdBy: addProductData.vendorId
			});
			if (dataObj.colorVariation) {
				if (variantPreferenceColorId) {
					await preferenceRequest('PUT', variantPreferenceColorId, prefBase('color', dataObj.colorVariation));
				} else {
					await preferenceRequest('POST', null, prefBase('color', dataObj.colorVariation));
				}
			}
			if (dataObj.color) {
				if (variantPreferenceColorCodeId) {
					await preferenceRequest('PUT', variantPreferenceColorCodeId, prefBase('colorCode', dataObj.color));
				} else {
					await preferenceRequest('POST', null, prefBase('colorCode', dataObj.color));
				}
			}
			if (dataObj.sizeVariation) {
				if (variantPreferenceSizeId) {
					await preferenceRequest('PUT', variantPreferenceSizeId, prefBase('size', dataObj.sizeVariation));
				} else {
					await preferenceRequest('POST', null, prefBase('size', dataObj.sizeVariation));
				}
			}

			// IMAGES
			await Promise.all([
				uploadImage(dataObj.variationImgPrimary, 'imgPrimary', variantIdValue),
				uploadImage(dataObj.variationImgSec1, 'imgSec1', variantIdValue),
				uploadImage(dataObj.variationImgSec2, 'imgSec2', variantIdValue),
				uploadImage(dataObj.variationImgSec3, 'imgSec3', variantIdValue),
				uploadImage(dataObj.variationImgSec4, 'imgSec4', variantIdValue)
			]);

			setVariationToShow(prev =>
				prev.map(v =>
					v.id === variantIdValue
						? { ...v, ...dataObj, variantCode: variantCodeStr }
						: v
				)
			);
			setOpenVariationModal(false);
			setOpenVariationModalEdit(false);
			setOpenSaveVariantSnackbar(true);
		} catch (err) {
			console.error(err);
			alert('Failed to save variant — please try again.');
		} finally {
			setLoadBackdrop(false);
		}
	};


	return (
		<Container maxWidth="lg" className={classes.inputNumber}>
			<ThemeProvider theme={theme}>
				<AppBar
					position="sticky"
					elevation={0}
					sx={{
						backdropFilter: 'blur(12px)',
						bgcolor: 'rgba(255,255,255,.8)',          // translucent white
						borderBottom: '1px solid #e5e7eb'        // subtle divider
					}}
				>
					<Toolbar sx={{ minHeight: { xs: 56, md: 64 } }}>
						<Button
							onClick={() => {
								saveSelection({
									departmentId: selectedDepartmentId,
									categoryId: selectedCategoryId,
									subCategoryId: selectedSubCategoryId,
								});
								navigate(-1);
							}}
							startIcon={<ArrowBackIosNewIcon />}
							variant="outlined"
							size="small" color="secondary"
							sx={{
								borderRadius: 999,
								textTransform: "none",
								px: 1.25, py: 0.75, fontWeight: 600, mr: 1,
								borderColor: "divider",
								bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
								"&:hover": { bgcolor: (t) => t.palette.action.hover },
							}}
						>
							Back
						</Button>

						<Box sx={{ flexGrow: 1 }}>
							<Typography variant={isMobile ? "h6" : "h5"}>
								{isMobile ? "Add New Product" : `${addProductData?.subCategory} • Add New Product`}
							</Typography>
							<Typography variant="caption">Product ID:&nbsp;{pID}</Typography>
						</Box>
						<Button
							startIcon={<Save />}
							disabled={!allRequiredMet}
							onClick={onSaveProduct}
							variant="contained"
							color="primary"
							sx={{
								textTransform: 'none',
								fontWeight: 600,
								px: { xs: 2, sm: 3 },
							}}
						>
							{isMobile ? 'Save' : 'Save Product'}
						</Button>
					</Toolbar>
				</AppBar>

				{/* ── Feedback ────────────────────────────────────── */}
				<Snackbar
					open={openSaveProductSnackBar}
					autoHideDuration={4000}
					onClose={handleCloseProductSnackbar}
				>
					<Alert severity="success" sx={{ width: "100%" }} onClose={handleCloseProductSnackbar}>
						Product saved successfully!
					</Alert>
				</Snackbar>

				<Snackbar
					open={openDuplicateImgSnackbar}
					autoHideDuration={3000}
					onClose={handleCloseDuplicateImgSnackbar}
				>
					<Alert
						severity="error"
						sx={{ width: '100%' }}
						onClose={handleCloseDuplicateImgSnackbar}
					>
						That image is already selected — please choose a different file.
					</Alert>
				</Snackbar>

				<Snackbar
					open={openSaveVariantSnackBar}
					autoHideDuration={4000}
					onClose={handleCloseVariantSnackbar}
				>
					<Alert severity="success" sx={{ width: "100%" }} onClose={handleCloseVariantSnackbar}>
						Variant saved successfully!
					</Alert>
				</Snackbar>

				<Snackbar
					open={openEnhanceWarn}
					autoHideDuration={3000}
					onClose={handleCloseEnhanceWarn}
				>
					<Alert
						severity="error"
						sx={{ width: '100%' }}
						onClose={handleCloseEnhanceWarn}
					>
						Enter both a product name and a description first, then click AI Enhance.
					</Alert>
				</Snackbar>

				<Snackbar
					open={Boolean(aiEnhanceError)}
					autoHideDuration={6000}
					onClose={(_, r) => r !== 'clickaway' && setAiEnhanceError("")}
				>
					<Alert
						severity="error"
						sx={{ width: '100%' }}
						onClose={() => setAiEnhanceError("")}
					>
						{aiEnhanceError}
					</Alert>
				</Snackbar>

				<Backdrop open={loadBackdrop} sx={{ color: "#fff", zIndex: theme.zIndex.drawer + 100 }}>
					<CircularProgress color="inherit" />
				</Backdrop>

				{/* ── Main Layout ─────────────────────────────────── */}
				<Box sx={{ p: 2 }}>
					<Grid container spacing={3}>
						{/* Sidebar (progress) – hidden on mobile */}
						{!isMobile && (
							<Grid item xs={12} md={3}>
								<Paper elevation={0} sx={{ p: 2, position: "sticky", top: 80 }}>
									<Typography variant="h6" gutterBottom>
										Progress
									</Typography>
									<LinearProgress
										variant="determinate"
										value={progressValue}
										sx={{
											height: 3,
											borderRadius: 3,
											[`& .MuiLinearProgress-bar`]: {
												bgcolor: '#0ea5e9'      // same blue as icons
											}
										}}
									/>
									<Typography variant="caption" display="block" gutterBottom>
										{completedSections} of {totalSections} sections completed
									</Typography>

									<List dense>
										{sections.map(({ id, label, icon: Icon }) => (
											<Box key={id}>
												{expanded === id && (
													<Box sx={{
														position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, color: '#fff',
														bgcolor: 'linear-gradient(90deg,#2563eb 0%,#7c3aed 100%)', borderRadius: '0 4px 4px 0',
													}} />
												)}
												<ListItemButton
													sx={{
														borderRadius: 2,
														...(expanded === id && {
															backgroundImage: 'linear-gradient(90deg,#2563eb 0%,#7c3aed 100%)',
															color: '#fff',
															'&:hover': { backgroundImage: 'linear-gradient(90deg,#1d4ed8 0%,#6d28d9 100%)' },
															boxShadow: '0 4px 8px rgba(0,0,0,.06)',
															'& .MuiSvgIcon-root': { color: '#fff' }
														})
													}}
													key={id}
													selected={expanded === id}
													onClick={() => setExpanded(id)}
												>
													<ListItemIcon>
														<Icon fontSize="small" />
													</ListItemIcon>
													<ListItemText primary={label} />
													{completed[id] && <Check fontSize="small" color="success" />}
												</ListItemButton>
											</Box>
										))}
									</List>

									{formErrors.length > 0 && (
										<Alert severity="warning" sx={{ mt: 2 }}>
											Complete all required sections to enable saving.
										</Alert>
									)}
								</Paper>
							</Grid>
						)}

						{/* ── Form Content ─────────────────────────────── */}
						<Grid item xs={12} md={9} gap={2}>
							{/* Product Details Accordion */}
							<Accordion
								disableGutters
								square elevation={0}
								sx={{ '&:before': { display: 'none' }, border: '1px solid #c9c9c9', borderRadius: 3 }}
								expanded={expanded === "panel1"}
								onChange={handleChange("panel1")}
							>
								<AccordionSummary expandIcon={<ExpandMore />}>
									<Stack direction="row" spacing={2} alignItems="center">
										<Avatar sx={{ bgcolor: '#dbeafe', width: 32, height: 32 }}>
											<Inventory2 sx={{ fontSize: 18, color: '#2563eb' }} />
										</Avatar>
										<Box>
											<Typography variant="subtitle1" fontWeight={600}>
												Product Details
											</Typography>
											<Typography variant="caption">
												Basic information about your product
											</Typography>
										</Box>
									</Stack>
								</AccordionSummary>

								<AccordionDetails>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<TextField size='small'
												label="Product Name *"
												fullWidth
												value={pName}
												onChange={(e) => setPName(e.target.value)}
												helperText={`${pName.length}/200 characters`}
												error={!pName && formErrors.length > 0}
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<TextField size='small'
												label="Display Name *"
												fullWidth
												value={pDisplayName}
												onChange={(e) => setPDisplayName(e.target.value)}
												helperText={`${pDisplayName.length}/200 characters`}
												error={!pDisplayName && formErrors.length > 0}
											/>
										</Grid>

										<Grid item xs={12}>
											<TextField size='small'
												label="Short Description"
												fullWidth
												multiline
												rows={2}
												value={pShortDesc}
												error={pShortDesc.length >= 250}
												onChange={(e) => setPShortDesc(e.target.value.slice(0, 250))}
												helperText={`${pShortDesc.length}/250 characters`}
											/>
										</Grid>

										<Grid item xs={12}>
											<Stack
												direction={{ xs: 'column', sm: 'row' }}
												justifyContent="space-between"
												alignItems={{ xs: 'flex-start', sm: 'center' }}
												spacing={1}
												mb={1}
											>
												<FormLabel>Product Description</FormLabel>
												<Button
													variant="outlined"
													size="small" color="secondary"
													startIcon={<AutoFixHighIcon fontSize="inherit" />}
													sx={{ textTransform: 'none' }}
													onClick={handleAiEnhanceClick}
												>
													AI Enhance
												</Button>
											</Stack>
											<TextField size='small'
												label="Product Description"
												fullWidth
												multiline
												rows={4}
												value={pDesc}
												onChange={(e) => setPDesc(e.target.value.slice(0, 350))}
												helperText={`${pDesc.length}/350 characters`}
											/>
										</Grid>
									</Grid>
								</AccordionDetails>
							</Accordion>

							<Box sx={{ mt: { xs: 2, sm: 3 } }}>
								<Accordion
									disableGutters
									square
									elevation={0}
									sx={{ '&:before': { display: 'none' }, border: '1px solid #c9c9c9', borderRadius: 3 }}
									expanded={expanded === "panel2"}
									onChange={handleChange("panel2")}
								>
									<AccordionSummary expandIcon={<ExpandMore />}>
										<Stack direction="row" spacing={2} alignItems="center">
											<Avatar sx={{ bgcolor: green[50] }}>
												<AttachMoney sx={{ color: green[600] }} />
											</Avatar>
											<Box>
												<Typography variant="subtitle1" fontWeight={600}>
													Pricing
												</Typography>
												<Typography variant="caption">
													Set product pricing
												</Typography>
											</Box>
										</Stack>
									</AccordionSummary>

									<AccordionDetails>
										<FormGroup>
											<FormControlLabel
												control={
													<Checkbox
														checked={pCSFlag}
														onChange={(e) => setPCSFlag(e.target.checked)}
													/>
												}
												label="Contact supplier for price"
											/>
										</FormGroup>

										<Grid container spacing={2} sx={{ mt: 1 }}>
											<Grid item xs={12} sm={6}>
												<TextField size='small'
													label="Barcode *"
													value={barCode}
													onChange={(e) => setBarCode(e.target.value)}
													fullWidth
													error={!barCode && formErrors.length > 0}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField size='small'
													label="SKU"
													value={pSKU}
													onChange={(e) => setPSKU(e.target.value)}
													fullWidth
												/>
											</Grid>

											<Grid item xs={12} sm={4}>
												<TextField size='small'
													label="Price / Unit"
													{...numberFieldProps}
													value={pricePerUnit ?? ""}
													onChange={clampNonNeg(setPricePerUnit)}
													fullWidth
													InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
													error={!pricePerUnit && formErrors.length > 0}
												/>
											</Grid>
											<Grid item xs={12} sm={4}>
												<TextField size='small'
													label="Units *"
													{...numberFieldProps}
													value={units ?? ""}
													onChange={clampNonNeg(setUnits)}
													fullWidth
													error={!units && formErrors.length > 0}
												/>
											</Grid>
											<Grid item xs={12} sm={4}>
												<TextField size='small'
													label="Quantity *"
													{...numberFieldProps}
													value={pQuantity ?? ""}
													onChange={clampNonNeg(setPQuantity)}
													fullWidth
													error={!pQuantity && formErrors.length > 0}
												/>
											</Grid>

											<Grid item xs={12} sm={6}>
												<TextField size='small'
													label="Total Price *"
													{...numberFieldProps}
													value={pTotalPrice ?? ""}
													onChange={clampNonNeg(setPTotalPrice)}
													fullWidth
													disabled={pCSFlag}
													InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
													error={!pTotalPrice && formErrors.length > 0 && !pCSFlag}
												/>
											</Grid>
										</Grid>
									</AccordionDetails>
								</Accordion>
							</Box>

							<Box sx={{ mt: { xs: 2, sm: 3 } }}>
								<Accordion
									disableGutters
									square
									elevation={0}
									sx={{ '&:before': { display: 'none' }, border: '1px solid #c9c9c9', borderRadius: 3 }}
									expanded={expanded === "panel3"}
									onChange={handleChange("panel3")}
								>
									<AccordionSummary expandIcon={<ExpandMore />}>
										<Stack direction="row" spacing={2} alignItems="center">
											<Avatar sx={{ bgcolor: purple[50] }}>
												<Image sx={{ color: purple[600] }} />
											</Avatar>
											<Box>
												<Typography variant="subtitle1" fontWeight={600}>
													Images
												</Typography>
												<Typography variant="caption">
													Upload up to 5 images
												</Typography>
											</Box>
										</Stack>
									</AccordionSummary>

									<AccordionDetails>
										<Link
											component="button"
											onClick={() => setGuideline(true)}
											underline="hover"
											sx={{ mb: 2, color: theme.palette.secondary.main, fontWeight: 600 }}
										>
											View image guidelines
										</Link>

										<Grid container spacing={2}>
											{Array.from({ length: Math.min(images.length + 1, 5) }).map((_, idx) => {
												const img = images[idx];          // will be undefined for the empty slot
												const inputRef = uploadInputRefs[idx];

												return (
													<Grid item xs={6} sm={4} md={3} key={idx}>
														{img ? (

															<Box position="relative">
																<CardActionArea
																	sx={uploadBoxSx}
																	onClick={() => window.open(img, '_blank')}
																>
																	<Box component="img" src={img} sx={previewSx} />
																</CardActionArea>

																<IconButton
																	size="small"
																	sx={deleteBtnSx}
																	onClick={() => removeImage(idx)}
																>
																	<Close fontSize="small" />
																</IconButton>

																{idx === 0 && (
																	<Chip
																		label="Primary"
																		size="small"
																		color="primary"
																		sx={{ position: 'absolute', bottom: 4, left: 4 }}
																	/>
																)}
															</Box>
														) : (
															/* ---------- empty upload slot ---------- */
															<CardActionArea
																sx={uploadBoxSx}
																onClick={() => inputRef.current?.click()}
															>
																<Add />
																<input
																	hidden
																	type="file"
																	accept="image/*"
																	ref={inputRef}
																	onChange={(e) => handleImageUpload(e, idx)}
																/>
															</CardActionArea>
														)}
													</Grid>
												);
											})}
										</Grid>
									</AccordionDetails>
								</Accordion>
							</Box>
							<Box sx={{ mt: { xs: 2, sm: 3 } }}>
								<Accordion
									disableGutters
									square
									elevation={0}
									sx={{ '&:before': { display: 'none' }, border: '1px solid #c9c9c9', borderRadius: 3 }}
									expanded={expanded === "panel4"}
									onChange={handleChange("panel4")}
								>
									<AccordionSummary expandIcon={<ExpandMore />}>
										<Stack direction="row" spacing={2} alignItems="center">
											<Avatar sx={{ bgcolor: orange[50] }}>
												<Warehouse sx={{ color: orange[600] }} />
											</Avatar>
											<Box>
												<Typography variant="subtitle1" fontWeight={600}>
													Inventory
												</Typography>
												<Typography variant="caption">
													Stock management
												</Typography>
											</Box>
										</Stack>
									</AccordionSummary>

									<AccordionDetails>
										<Grid container spacing={2}>
											<Grid item xs={12} sm={4}>
												<TextField size='small'
													label="Maximum Stock *"
													{...numberFieldProps}
													value={maxStock ?? ""}
													onChange={clampNonNeg(setMaxStock)}
													fullWidth
													error={!maxStock && formErrors.length > 0}
												/>
											</Grid>
											<Grid item xs={12} sm={4}>
												<TextField size='small'
													label="In Stock *"
													{...numberFieldProps}
													value={pInStock ?? ""}
													onChange={clampNonNeg(setPInStock)}
													fullWidth
													error={!pInStock && formErrors.length > 0}
												/>
											</Grid>
											<Grid item xs={12} sm={4}>
												<TextField size='small'
													label="Low Stock Threshold *"
													{...numberFieldProps}
													value={pLowStock ?? ""}
													onChange={clampNonNeg(setPLowStock)}
													fullWidth
													error={!pLowStock && formErrors.length > 0}
												/>
											</Grid>
										</Grid>

										<FormControlLabel
											control={
												<Checkbox
													checked={pLowStockTrack}
													onChange={(e) => setPLowStockTrack(e.target.checked)}
												/>
											}
											label="Enable low-stock email notifications"
											sx={{ mt: 1 }}
										/>
									</AccordionDetails>
								</Accordion>
							</Box>
							<Box sx={{ mt: { xs: 2, sm: 3 } }}>
								<Accordion
									disableGutters
									square
									elevation={0}
									sx={{ '&:before': { display: 'none' }, border: '1px solid #c9c9c9', borderRadius: 3 }}
									expanded={expanded === "panel5"}
									onChange={handleChange("panel5")}
								>
									<AccordionSummary expandIcon={<ExpandMore />}>
										<Stack direction="row" spacing={2} alignItems="center">
											<Avatar sx={{ bgcolor: indigo[50] }}>
												<Layers sx={{ color: indigo[600] }} />
											</Avatar>
											<Box>
												<Typography variant="subtitle1" fontWeight={600}>
													Variations
												</Typography>
												<Typography variant="caption">
													Sizes, colours, etc.
												</Typography>
											</Box>
										</Stack>
									</AccordionSummary>

									<AccordionDetails>
										{/* ── MAIN PRODUCT COLOUR & SIZE ─────────────────────── */}
										<Grid container spacing={2} sx={{ mb: 2 }}>
											{/* Colour swatch grid */}
											<Grid item xs={12} md={6}>
												<FormLabel>Available Colour</FormLabel>
												<Grid container spacing={1} sx={{ mt: 1, maxWidth: 280 }}>
													{colourSwatches.map((hex) => (
														<Grid item xs={2} key={hex}>
															<IconButton
																size="small"
																onClick={() => handleProductColourSelect(hex)}
																sx={{
																	width: 32, height: 32, borderRadius: 1,
																	bgcolor: hex,
																	border: pColorValue === hex ? '2px solid #2563eb' : '1px solid #e5e7eb',
																	transform: pColorValue === hex ? 'scale(1.15)' : 'none',
																	transition: 'all .15s'
																}}
															/>
														</Grid>
													))}
													{/* custom colour picker */}
													<Grid item xs={4}>
														<input
															type="color"
															value={pColorValue || '#000000'}
															onChange={(e) => handleProductColourSelect(e.target.value, true)}
															style={{ width: '100%', height: 32, border: 'none', padding: 0 }}
														/>
													</Grid>
												</Grid>
												{pColor && (
													<Typography variant="caption" sx={{ mt: .5, display: 'block' }}>
														Selected: {pColor}
													</Typography>
												)}
											</Grid>

											{/* Size selector */}
											<Grid item xs={12} md={6}>
												<FormLabel>Available Size</FormLabel>
												<Autocomplete
													freeSolo
													options={sizeOptions}
													groupBy={(o) => o.category}
													getOptionLabel={(o) => typeof o === 'string' ? o : o.label}
													sx={{ mt: 1, maxWidth: 300 }}
													value={sizeOptions.find((o) => o.label === pSize) || pSize}
													onChange={(_, val) => {
														if (typeof val === 'string') {
															setPSize(val)
														} else if (val && val.inputValue) {
															setPSize(val.inputValue)
														} else {
															setPSize(val ? val.label : '')
														}
													}}
													renderInput={(params) => <TextField size='small' {...params} label="Choose Size" />}
													filterOptions={(opts, params) => {
														const filtered = filterOptions(opts, params);
														const exists = opts.some(o => params.inputValue === o.label);
														if (params.inputValue !== '' && !exists) {
															filtered.push({ label: `${params.inputValue}`, value: params.inputValue, category: 'Custom' });
														}
														return filtered;
													}}
												/>
											</Grid>
										</Grid>
										<Divider sx={{ mb: 2 }} />
										<Typography variant='body1' sx={{ fontWeight: 'bold' }}>Add Variants for {pName ? pName : "this product"}</Typography>
										<Box display="flex" justifyContent="flex-end" mb={2}>
											<Button color="secondary" variant='contained' startIcon={<Add />} onClick={handleAddVariant}>
												Add Variant
											</Button>
										</Box>

										{variationToShow.length === 0 ? (
											<Typography variant="body2" color="text.secondary">
												No variants added yet.
											</Typography>
										) : (
											<Grid container spacing={2}>
												{variationToShow.map((variant) => (
													<Grid item xs={12} sm={6} md={4} key={variant.id}>
														<Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
															<Stack direction="row" justifyContent="space-between" alignItems="center">
																<Typography fontWeight={600}>
																	{variant.variantCode ||
																		[variant.colorVariation, variant.sizeVariation].filter(Boolean).join(' • ') ||
																		"Untitled"}
																</Typography>
																<Stack direction="row" spacing={1}>
																	<IconButton size="small" onClick={() => editVariant(variant)}>
																		<Edit fontSize="small" />
																	</IconButton>
																	<IconButton size="small" onClick={() => deleteVariant(variant.id)}>
																		<Delete fontSize="small" />
																	</IconButton>
																</Stack>
															</Stack>

															<Typography variant="caption" display="block">
																Stock {variant.variationInStock ?? 0} | Price ₹ {variant.variationTotalPrice ?? 0}
															</Typography>
														</Paper>
													</Grid>
												))}
											</Grid>
										)}
									</AccordionDetails>
								</Accordion>
							</Box>
							<Box sx={{ mt: { xs: 2, sm: 3 } }}>
								<Accordion
									disableGutters
									square
									elevation={0}
									sx={{ '&:before': { display: 'none' }, border: '1px solid #c9c9c9', borderRadius: 3 }}
									expanded={expanded === "panel6"}
									onChange={handleChange("panel6")}
								>
									<AccordionSummary expandIcon={<ExpandMore />}>
										<Stack direction="row" spacing={2} alignItems="center">
											<Avatar sx={{ bgcolor: teal[50] }}>
												<LocalShipping sx={{ color: teal[600] }} />
											</Avatar>
											<Box>
												<Typography variant="subtitle1" fontWeight={600}>
													Shipping Fulfilment
												</Typography>
											</Box>
										</Stack>
									</AccordionSummary>

									<AccordionDetails>
										<Grid container spacing={2}>
											<Grid item xs={12} sm={6}>
												<FormControl fullWidth>
													<InputLabel>Fulfilment *</InputLabel>
													<Select
														label="Fulfilment *"
														value={pFulfillment}
														onChange={(e) => setPFulfillment(e.target.value)}
														error={!pFulfillment && formErrors.length > 0}
													>
														<MenuItem value="Standard Shipping">Standard Shipping</MenuItem>
														<MenuItem value="Standard Shipping and Returns">
															Standard Shipping and Returns
														</MenuItem>
													</Select>
												</FormControl>
											</Grid>

											<Grid item xs={12} sm={6}>
												<FormControl fullWidth>
													<InputLabel>Status *</InputLabel>
													<Select
														label="Status *"
														value={pStatus}
														onChange={(e) => setPStatus(e.target.value)}
														error={!pStatus && formErrors.length > 0}
													>
														<MenuItem value="Available">Available</MenuItem>
														<MenuItem value="Private">Private (Hidden)</MenuItem>
														<MenuItem value="Unavailable">Unavailable</MenuItem>
													</Select>
												</FormControl>
											</Grid>

											<Grid item xs={12}>
												<FormControl fullWidth>
													<InputLabel>Delivery Options *</InputLabel>
													<Select
														multiple
														value={pDeliveryOption}
														onChange={(e) => setPDeliveryOption(e.target.value)}
														input={<OutlinedInput label="Delivery Options *" />}
														renderValue={(sel) => sel.join(", ")}
														error={pDeliveryOption.length === 0 && formErrors.length > 0}
													>
														{deliveryOption.map((opt) => (
															<MenuItem key={opt} value={opt}>
																<Checkbox checked={pDeliveryOption.indexOf(opt) > -1} />
																<ListItemText primary={opt} />
															</MenuItem>
														))}
													</Select>
												</FormControl>
											</Grid>

											<Grid item xs={12} sm={6}>
												<TextField size='small'
													label="Shipping Weight (Kg)"
													{...numberFieldProps}
													value={pShippingWeight ?? ""}
													onChange={clampNonNeg(setPShippingWeight)}
													fullWidth
												/>
											</Grid>
										</Grid>
									</AccordionDetails>
								</Accordion>
							</Box>
							<Box sx={{ mt: { xs: 2, sm: 3 } }}>
								<Accordion
									disableGutters
									square
									elevation={0}
									sx={{ '&:before': { display: 'none' }, border: '1px solid #c9c9c9', borderRadius: 3 }}
									expanded={expanded === "panel7"}
									onChange={handleChange("panel7")}
								>
									<AccordionSummary expandIcon={<ExpandMore />}>
										<Stack direction="row" spacing={2} alignItems="center">
											<Avatar sx={{ bgcolor: grey[200] }}>
												<InfoIcon color="action" />
											</Avatar>
											<Typography variant="subtitle1" fontWeight={600}>
												Additional Information
											</Typography>
										</Stack>
									</AccordionSummary>

									<AccordionDetails>
										<Autocomplete
											multiple
											freeSolo
											options={additionalInformations}
											value={pAdditionalInfo}
											filterOptions={(options, params) => {
												const filtered = createFilterOptions()(options, params);
												const { inputValue } = params;
												const isExisting = options.some((option) => inputValue === option);
												if (inputValue !== '' && !isExisting) {
													filtered.push(`Add "${inputValue}"`);
												}
												return filtered;
											}}
											getOptionLabel={(option) => {
												if (typeof option === 'string' && option.startsWith('Add "')) {
													return option.replace('Add "', '').replace('"', '');
												}
												return option;
											}}
											onChange={(event, newValue) => {
												const finalValues = newValue.map(val => {
													if (typeof val === 'string' && val.startsWith('Add "')) {
														return val.replace('Add "', '').replace('"', '');
													}
													return val;
												});
												setPAdditionalInfo(finalValues);
											}}
											renderTags={(value, getTagProps) =>
												value.map((option, index) => (
													<Chip variant="outlined" label={option} {...getTagProps({ index })} />
												))
											}
											renderInput={(params) => <TextField size='small' {...params}
												label="Special indicators"
												helperText="Type and press enter to add values"
											/>}
											sx={{ mb: 3 }}
										/>
									</AccordionDetails>
								</Accordion>
							</Box>
							{/* ───────────────────────────────────────────── Dialogs / Modals */}
							<Dialog open={openSaveProduct} onClose={() => setOpenSaveProduct(false)}>
								<DialogTitle>Missing Information</DialogTitle>
								<DialogContent dividers>
									<Typography variant="body2" component="ul" sx={{ pl: 2 }}>
										{formErrors.map((err, i) => (
											<li key={i}>{err}</li>
										))}
									</Typography>
								</DialogContent>
								<DialogActions>
									<Button onClick={() => setOpenSaveProduct(false)}>Close</Button>
								</DialogActions>
							</Dialog>

							<ImageGuidelinesDialog
								open={guideline}
								onClose={() => setGuideline(false)}
							/>

							<Dialog open={saveFailureOpen} onClose={() => setSaveFailureOpen(false)}>
								<DialogTitle>Some parts did not save</DialogTitle>
								<DialogContent dividers>
									<Typography variant="body2" sx={{ mb: 1 }}>
										Everything else was saved. Retry sends only the parts below.
									</Typography>
									<Typography variant="body2" component="ul" sx={{ pl: 2 }}>
										{saveFailures.map((f) => (
											<li key={f.step}>
												{SAVE_STEP_LABELS[f.step] || f.step}: {f.message}
											</li>
										))}
									</Typography>
								</DialogContent>
								<DialogActions>
									<Button onClick={() => setSaveFailureOpen(false)}>Close</Button>
									<Button variant="contained" onClick={retryFailedSave}>Retry failed</Button>
								</DialogActions>
							</Dialog>

							{/* Variant add/edit modal */}
							<VariantDialog
								open={openVariationModal || openVariationModalEdit}
								onClose={() => {
									setOpenVariationModal(false);
									setOpenVariationModalEdit(false);
								}}
								isEdit={openVariationModalEdit}
								variation={variationDetails}
								onSave={openVariationModalEdit ? handleSaveVariantData : handleAddVariantData}
								onChange={handleVariationInputChange}
								handlers={{
									handleVariantPrimaryUpload,
									handleVariantSec1Upload,
									handleVariantSec2Upload,
									handleVariantSec3Upload,
									handleVariantSec4Upload
								}}
								inputRefs={{        /* NEW */
									variantInput1,
									variantInput2,
									variantInput3,
									variantInput4,
									variantInput5
								}}
								images={{
									variantPrimary,
									variantSec1,
									variantSec2,
									variantSec3,
									variantSec4
								}}
								removeImage={(idx) => {
									if (idx === 0) setVariantPrimary(null);
									else if (idx === 1) setVariantSec1(null);
									else if (idx === 2) setVariantSec2(null);
									else if (idx === 3) setVariantSec3(null);
									else setVariantSec4(null);
								}}
							/>
						</Grid>
					</Grid>
				</Box>
			</ThemeProvider>
		</Container>
	);
}
